import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { BookOpen, Eye, EyeOff, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01";

const EbookLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  
  const { login, isAuthenticated, hasEbookAccess, isLoading: authLoading } = useReaderAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      if (redirectTo === 'buy') {
        window.location.href = STRIPE_PAYMENT_LINK;
      } else if (hasEbookAccess()) {
        navigate('/ebook/library');
      } else {
        navigate('/ebook');
      }
    }
  }, [authLoading, isAuthenticated, hasEbookAccess, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const result = await login(email, password);
    
    if (result.success) {
      if (redirectTo === 'buy') {
        window.location.href = STRIPE_PAYMENT_LINK;
      } else {
        navigate('/ebook/library');
      }
    } else {
      setError(result.error);
    }
    
    setIsSubmitting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center p-6">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/ebook" className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <BookOpen size={32} />
          </Link>
          <h1 className="text-2xl font-bold text-[#2c3e50] font-display">
            Sign In to Read
          </h1>
          <p className="text-[#5a6c7d] mt-2">
            Access your eBook library
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#2c3e50] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#2c3e50] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 focus:border-[#d4af37] transition-all"
                  placeholder="you@example.com"
                  required
                  data-testid="ebook-login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2c3e50] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#2c3e50] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 focus:border-[#d4af37] transition-all"
                  placeholder="Enter your password"
                  required
                  data-testid="ebook-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-[#d4af37] text-[#2c3e50] font-semibold rounded-xl hover:bg-[#c9a432] focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              data-testid="ebook-login-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#5a6c7d] text-sm">
              Don't have an account?{' '}
              <Link 
                to={`/ebook/register${redirectTo ? `?redirect=${redirectTo}` : ''}`}
                className="text-[#d4af37] font-medium hover:underline"
              >
                Create one
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/ebook" className="text-[#5a6c7d] text-sm hover:text-[#2c3e50]">
            ← Back to eBook
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EbookLogin;
